import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';
import { injectIntl, InjectedIntl } from 'react-intl';
import { IntlProvider as IntlNextProvider } from 'react-intl-next';

import { AnalyticsEvent, AnalyticsListener } from '@atlaskit/analytics-next';
import {
  MultiProductDialog,
  BitbucketTab,
} from '@atlassian/product-search-dialog';

import { PageContext } from '../../types';
import url from '../../urls';
import ReposContext from '../contexts';
import FlagGroup, { FlagGroupItem } from '../flag-group';

import { HorizontalNavContext } from './horizontal-nav-context';
import HorizontalPlatformSearchCompassTab from './horizontal-platform-search-compass-tab';
import HorizontalPlatformSearchCompassTabUpsell from './horizontal-platform-search-compass-tab-upsell';
import messages from './horizontal-platform-search.i18n';

export const EMPTY_SEARCH_ITEMS = {
  size: 0,
  sections: [],
};

type Props = {
  intl: InjectedIntl;
  /* eslint-disable react/no-unused-prop-types */
  isExpanded: boolean;
};

// XXX This search component seems to hold onto the very first values from the
// ReposContext and not get updated. The effect of that is that we may have
// already loaded repositories on the dashboard, but this component won't see
// them. The current state is that we always show the empty placeholder, but
// these changes mean we'll make a request for the recent repos. Even though
// that's a duplicate request on the dashboard page, it's an improvement on all
// other pages.
const mapBitbucketReposToSearchItems = async (
  repositories: BB.Repository[],
  loadRepositories: (includeStatus: boolean) => Promise<BB.Repository[]>,
  title: string
) => {
  let repos = repositories;
  if (!repos.length) {
    repos = await loadRepositories(false);
  }

  if (!repos.length) {
    // If we've tried loading repos and still have no results, return the
    // empty placeholder
    return EMPTY_SEARCH_ITEMS;
  }

  return {
    size: repos.length,
    sections: [
      {
        id: 'bitbucket.repository',
        title,
        searchResults: repos.map((repo: BB.Repository) => {
          return {
            title: repo.name,
            id: repo.uuid,
            meta: repo?.workspace?.name || '',
            url: repo.links.html.href,
            iconUrl: repo.links.avatar.href,
          };
        }),
      },
    ],
  };
};

const onNavigate = (_productId: string, href: string) => {
  window.location.href = href;
};

const HorizontalPlatformSearch = (props: Props) => {
  const ref = useRef<HTMLInputElement>(null);
  const { repositories, loadRepositories } = useContext(ReposContext);
  const [cloudId, setCloudId] = useState<string>('');
  const [isLoadingCloudId, setIsLoadingCloudId] = useState<boolean>(false);
  const [flags, setFlags] = useState<FlagGroupItem[]>([]);
  const {
    currentRepository,
    currentWorkspace,
    isLoggedIn,
    isSearchDrawerOpen,
    isWorkspaceAdmin = false,
    onSearchDrawerClose,
    onSearchDrawerOpen,
    publishUiEvent,
  } = useContext(HorizontalNavContext);

  const workspaceSlug = currentWorkspace?.slug || '';
  const showCompassTab = useMemo(
    () => isLoggedIn && !!workspaceSlug,
    [isLoggedIn, workspaceSlug]
  );

  useEffect(() => {
    if (!showCompassTab) {
      return;
    }

    const fetchCloudData = async () => {
      setIsLoadingCloudId(true);
      const response = await fetch(
        url.api.internal.getCompassCloudIdForWorkspace(workspaceSlug)
      );
      const result = await response.json();

      if (!result.connectedCloudIds || result.connectedCloudIds.length === 0) {
        setIsLoadingCloudId(false);
        return;
      }
      setCloudId(result.connectedCloudIds[0]);
      setIsLoadingCloudId(false);
    };

    try {
      fetchCloudData();
    } catch (e) {
      Sentry.captureException(e);
      setCloudId('');
      setIsLoadingCloudId(false);
    }
  }, [workspaceSlug, showCompassTab]);

  // Allows for global keyboard shortcut '/' to also toggle the horizontal nav search
  const setIsExpanded = () => {
    if (isSearchDrawerOpen) {
      onSearchDrawerClose();
    } else {
      if (onSearchDrawerOpen) {
        onSearchDrawerOpen();
      }
    }
  };

  const currentContext: PageContext = {
    repository: currentRepository,
    workspace: currentWorkspace,
  };

  const publishAnalyticsEvent = (event: AnalyticsEvent) => {
    const { action, actionSubject, actionSubjectId } = event.payload;
    if (!event.payload.tags) {
      event.payload.tags = [];
    }
    event.payload.tags.push('fabricElements');
    publishUiEvent({
      action,
      actionSubject,
      actionSubjectId,
      ...event.payload,
      source: 'search',
    });
  };

  const onEvent = (event: AnalyticsEvent) => {
    // Do not fire event if event.payload.action is not defined. For some reason
    // this value is undefined on some of the events from the `fabric-elements`
    // channel. Adding this check to prevents a TON of sentry errors (message
    // is "Missing event.action"... so far we've had 5M+). TODO: Investigate why
    // this is sometimes undefined and how to fix (COREX-5539)
    if (!event.payload.action) {
      return;
    }

    try {
      publishAnalyticsEvent(event);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const CompassTab = (compassTabProps: { onRetry: () => void }) => {
    if (cloudId) {
      return (
        <HorizontalPlatformSearchCompassTab
          cloudId={cloudId}
          onRetry={compassTabProps.onRetry}
        />
      );
    }
    return (
      <HorizontalPlatformSearchCompassTabUpsell
        flags={flags}
        intl={props.intl}
        isWorkspaceAdmin={isWorkspaceAdmin}
        onRetry={compassTabProps.onRetry}
        preSetFlagCallback={setIsExpanded}
        setFlags={setFlags}
        workspaceSlug={workspaceSlug}
      />
    );
  };

  return (
    <IntlNextProvider locale="en">
      <AnalyticsListener channel="fabric-elements" onEvent={onEvent}>
        <MultiProductDialog
          isExpanded={isSearchDrawerOpen}
          setIsExpanded={setIsExpanded}
          abTestCloudId={cloudId || 'bitbucket-cloud'}
          aggregatorUrl={url.api.gateway.repoSearch()}
          forwardRef={ref}
          onNavigateGeneric={onNavigate}
        >
          {({ onRetry }: { onRetry: () => void }) => (
            <>
              <BitbucketTab
                onRetry={onRetry}
                order={0}
                // TODO: use recent repos API for preQueryItemSupplier when recent repos service will be done
                preQueryItemSupplier={() =>
                  mapBitbucketReposToSearchItems(
                    repositories,
                    loadRepositories,
                    props.intl.formatMessage(messages.repositoriesHeading)
                  )
                }
                isPrefetchingEnabled={false}
                viewAllLinkGenerator={url.ui.repositories}
                urlGeneratorForNoResultsScreen={url.ui.search}
                codeSearchUrlGenerator={url.ui.search}
                permissionSupplier={() =>
                  Promise.resolve(['bitbucket.repository'])
                }
                currentContext={currentContext}
              />
              {showCompassTab && !isLoadingCloudId && (
                <CompassTab onRetry={onRetry} />
              )}
            </>
          )}
        </MultiProductDialog>
        <FlagGroup setFlags={setFlags} flags={flags} />
      </AnalyticsListener>
    </IntlNextProvider>
  );
};

export default React.memo(injectIntl(HorizontalPlatformSearch));
